import { useState, useEffect } from "react";
import { FounderBadge } from "@/components/FoundersClub";
import { isNative } from "@/lib/native";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import InspectionCountdown from "@/components/InspectionCountdown";
import ResourceFileList from "@/components/ResourceFileList";
import ReadinessScore from "@/components/ReadinessScore";
import InspectorMode from "@/components/InspectorMode";
import PolicyReviewCard from "@/components/PolicyReviewCard";
import ManageBilling from "@/components/ManageBilling";
import InstallPrompt from "@/components/InstallPrompt";
import DirectLineCard from "@/components/DirectLineCard";
import OnboardingTour from "@/components/OnboardingTour";
import ReferralCard from "@/components/ReferralCard";
import PushToggle from "@/components/PushToggle";
import ShareExperience from "@/components/ShareExperience";
import {
  Crown, FileText, MessageSquareText, LifeBuoy, ArrowRight,
  Sparkles, CheckCircle2, Clock, Loader2, Package, GraduationCap, Users, FolderOpen, ClipboardCheck, BookOpenCheck, Receipt, Siren,
} from "lucide-react";

const TOOLKIT = [
  { to: "/evidence", icon: FolderOpen, title: "Evidence folder", desc: "File transcripts, reports and audits under each key question", id: "evidence" },
  { to: "/training", icon: BookOpenCheck, title: "Staff training log", desc: "Mandatory training matrix with expiry alerts", id: "training" },
  { to: "/inspections", icon: ClipboardCheck, title: "Inspection debrief", desc: "Record outcomes and track your improvement plan", id: "inspections" },
];

const TIER_LABEL = { support: "Ongoing Support", starter: "Starter Support", professional: "Professional Manager", enterprise: "Enterprise Multi-site" };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/me/purchases").then((r) => setData(r.data)),
      api.get("/escalations/mine").then((r) => setEscalations(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const tier = data?.subscription_tier;
  const active = data?.subscription_status === "active";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <span className="overline text-accent">Dashboard</span>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          Welcome back, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="text-muted-foreground mt-1">{user?.org_name || "Your compliance workspace"}</p>
        <FounderBadge className="mt-3" />
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm min-w-0" data-testid="dashboard-subscription-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Subscription</span>
            <Crown className={`w-5 h-5 ${active ? "text-amber-500" : "text-muted-foreground"}`} />
          </div>
          <p className="font-display text-xl font-bold">{active && tier ? TIER_LABEL[tier] : data?.subscription_status === "expired" ? "Plan ended" : "No plan"}</p>
          {active ? (
            <div className="mt-2 space-y-1">
              <Badge variant="secondary" className="bg-accent/10 text-accent border-0">{data?.cancel_at_period_end ? "Ends" : "Active"}</Badge>
              {user?.team_owner_id && <p className="text-xs text-muted-foreground" data-testid="subscription-via-team">Via {user.team_owner_name}'s team</p>}
              {data?.subscription_period_end && !user?.team_owner_id && (
                <p className="text-xs text-muted-foreground" data-testid="subscription-renewal-date">
                  {data.cancel_at_period_end ? "Access until" : "Renews"} {new Date(data.subscription_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              )}
              <ManageBilling onChanged={() => api.get("/me/purchases").then((r) => setData(r.data))} />
              {!isNative && (
              <Link to="/billing" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1" data-testid="dashboard-billing-link">
                <Receipt className="w-3.5 h-3.5" /> Billing & invoices
              </Link>
              )}
              {(tier === "enterprise" || user?.team_owner_id) && (
                <Link to="/team" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1" data-testid="dashboard-team-link">
                  <Users className="w-3.5 h-3.5" /> {user?.team_owner_id ? "Your team" : "Manage team seats"}
                </Link>
              )}
            </div>
          ) : (
            !isNative && <Link to="/pricing" className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1" data-testid="dashboard-choose-plan-link">
              {data?.subscription_status === "expired" ? "Renew your plan" : "Choose a plan"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm min-w-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Resources owned</span>
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <p className="font-display text-3xl font-extrabold">{data?.resources?.length || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm min-w-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Specialist queries</span>
            <LifeBuoy className="w-5 h-5 text-primary" />
          </div>
          <p className="font-display text-3xl font-extrabold">{escalations.length}</p>
        </div>
        <Link to="/assistant" className="bg-primary text-primary-foreground rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="dashboard-ask-ai-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm opacity-90">Ask the AI</span>
            <Sparkles className="w-5 h-5" />
          </div>
          <p className="font-display text-lg font-bold flex items-center gap-1">
            Open assistant <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </p>
        </Link>
      </div>

      <InstallPrompt />
      <OnboardingTour />
      <p className="text-xs text-muted-foreground mt-8 text-center">New here? <Link to="/tour" className="underline hover:text-foreground" data-testid="dashboard-tour-link">Take the 8-step product tour</Link></p>

      <div className="mb-8 space-y-6">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2"><ReadinessScore refreshKey={escalations.length} /></div>
          <DirectLineCard />
        </div>
        <ShareExperience />
        <PushToggle />
        <ReferralCard />
        <div className="grid sm:grid-cols-3 gap-4" data-testid="toolkit-tiles">
          {TOOLKIT.map((t) => (
            <Link key={t.id} to={t.to} className="group bg-card border border-border rounded-2xl p-5 shadow-sm hover:border-primary/40 hover:shadow-md transition-all" data-testid={`toolkit-tile-${t.id}`}>
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3"><t.icon className="w-5 h-5" /></div>
              <p className="font-display font-semibold flex items-center gap-1">{t.title} <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></p>
              <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
            </Link>
          ))}
        </div>
        <InspectorMode />
        <PolicyReviewCard />
        <InspectionCountdown />
        <Link to="/inspection-day" className="mt-3 inline-flex items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/10 transition-colors" data-testid="inspection-day-link"><Siren className="w-4 h-4" /> Inspector here today? Open Inspection Day mode</Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Purchased resources */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm min-w-0">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" /> Your library
            </h2>
            {data?.resources?.length ? (
              <div className="space-y-3">
                {data.resources.map((r) => (
                  <div key={r.id} className="p-3 rounded-xl bg-secondary/50" data-testid={`owned-resource-row-${r.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{r.title}</p>
                        <p className="text-xs text-muted-foreground">{r.category}</p>
                      </div>
                      {r.type === "mock_interview" && (
                        <Button asChild size="sm" variant="outline" className="shrink-0">
                          <Link to="/mock-interview"><GraduationCap className="w-4 h-4 mr-1.5" /> Start</Link>
                        </Button>
                      )}
                    </div>
                    {r.type !== "mock_interview" && <ResourceFileList resourceId={r.id} files={r.files} />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground mb-3">You haven't purchased any resources yet.</p>
                <Button asChild variant="outline" size="sm"><Link to="/resources">Browse the store</Link></Button>
              </div>
            )}
          </div>

          {/* Escalation history */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm min-w-0">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-primary" /> Specialist queries
            </h2>
            {escalations.length ? (
              <div className="space-y-3">
                {escalations.map((e) => (
                  <div key={e.id} className="p-4 rounded-xl border border-border" data-testid={`escalation-row-${e.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <p className="text-sm font-medium line-clamp-2">{e.question}</p>
                      <Badge variant="secondary" className={e.status === "answered" ? "bg-accent/10 text-accent border-0 shrink-0" : "shrink-0"}>
                        {e.status === "answered" ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Answered</> : <><Clock className="w-3 h-3 mr-1" /> Open</>}
                      </Badge>
                    </div>
                    {e.admin_response && (
                      <div className="mt-2 text-sm bg-accent/5 border border-accent/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-accent mb-1">Specialist reply</p>
                        <p className="text-muted-foreground whitespace-pre-wrap">{e.admin_response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground mb-3">No specialist queries yet.</p>
                <Button asChild variant="outline" size="sm"><Link to="/assistant">Ask the assistant</Link></Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
